import { useEffect, useMemo, useState } from 'react'
import {
  ArrowRight,
  Briefcase,
  CalendarDays,
  CarFront,
  Clock3,
  MapPin,
  Users,
  X,
  Star,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import ProfileModalShell from '../../components/ProfileModalShell'
import {
  apiGetUserTrips,
  apiCancelUserTrip,
  type UserTrip as Trip,
  type TripStatus,
} from '../../api/profile/userProfileApi'
import {
  isTripReviewed,
  getTripReview,
  TRIP_REVIEW_TRIGGER_EVENT,
  TRIP_REVIEW_SUBMITTED_EVENT,
} from '../../utils/tripReview'

type OverlayStep = 'details' | 'confirm' | 'success' | null

export default function MyTrips() {
  const navigate = useNavigate()
  const [trips, setTrips] = useState<Trip[]>([])
  const [loading, setLoading] = useState(true)
  // const [statusFilter, setStatusFilter] = useState<'All Status' | TripStatus>(
  //   'All Status',
  // )
  // const [dateFilter, setDateFilter] = useState<'Date Range' | 'This Week' | 'Past'>('Date Range')
  // const [statusOpen, setStatusOpen] = useState(false)
  // const [dateOpen, setDateOpen] = useState(false)
  const [overlayStep, setOverlayStep] = useState<OverlayStep>(null)
  const [selectedTripId, setSelectedTripId] = useState<number | null>(null)
  const [reason, setReason] = useState('')
  const [policyAccepted, setPolicyAccepted] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [, setReviewUpdate] = useState(0)

  // Listen for review submission to refresh reviewed badges
  useEffect(() => {
    const handleReviewSubmitted = () => {
      setReviewUpdate((v) => v + 1)
    }
    window.addEventListener(TRIP_REVIEW_SUBMITTED_EVENT, handleReviewSubmitted)
    return () => {
      window.removeEventListener(TRIP_REVIEW_SUBMITTED_EVENT, handleReviewSubmitted)
    }
  }, [])

  // Fetch trips from API
  useEffect(() => {
    let mounted = true
    const loadTrips = async () => {
      setLoading(true)
      try {
        const res = await apiGetUserTrips()
        if (mounted && res.success && Array.isArray(res.data)) {
          setTrips(res.data)
        }
      } catch (err) {
        console.error('Failed to load trips', err)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    loadTrips()
    return () => {
      mounted = false
    }
  }, [])

  // const filteredTrips = useMemo(() => {
  //   return trips.filter((trip) => {
  //     if (statusFilter !== 'All Status' && trip.status !== statusFilter) return false
  //     if (dateFilter === 'This Week' && trip.status === 'Cancelled') return false
  //     if (dateFilter === 'Past' && trip.status !== 'Cancelled') return false
  //     return true
  //   })
  // }, [statusFilter, dateFilter, trips])

  const selectedTrip = useMemo(
    () => trips.find((trip) => trip.id === selectedTripId) ?? null,
    [selectedTripId, trips],
  )

  const closeOverlay = () => {
    setOverlayStep(null)
    setSelectedTripId(null)
    setReason('')
    setPolicyAccepted(false)
  }

  useEffect(() => {
    if (!overlayStep) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeOverlay()
    }

    document.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleEscape)
    }
  }, [overlayStep])

  const openDetails = (tripId: number) => {
    setSelectedTripId(tripId)
    setOverlayStep('details')
  }

  const openCancellation = (tripId: number) => {
    setSelectedTripId(tripId)
    setReason('')
    setPolicyAccepted(false)
    setOverlayStep('confirm')
  }

  const confirmCancellation = async () => {
    if (!selectedTrip || !policyAccepted || cancelling) return

    setCancelling(true)
    try {
      await apiCancelUserTrip(selectedTrip.id, reason)
    } catch {}

    setTrips((current) =>
      current.map((trip) =>
        trip.id === selectedTrip.id ? { ...trip, status: 'Cancelled' } : trip,
      ),
    )
    setCancelling(false)
    setOverlayStep('success')
  }

  return (
    <>
      <ProfileModalShell>
        <section className="w-full max-w-[847px]">
          <h1 className="font-host text-[28px] font-semibold tracking-[-0.035em] text-[#242424] sm:text-[31px]">
            My Trips
          </h1>
          <p className="mt-2 font-host text-[15px] tracking-[0.01em] text-[#858585] sm:text-[16px]">
            Manage all your upcoming and completed bookings in one place.
          </p>

          {/* <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setStatusOpen((open) => !open)
                  setDateOpen(false)
                }}
                className="inline-flex h-[42px] min-w-[142px] items-center justify-between gap-3 rounded-[7px] bg-white px-4 font-host text-[13px] font-medium text-[#161616] shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 stroke-[2]" />
                  {statusFilter}
                </span>
                <ChevronDown className="h-4 w-4 stroke-[2]" />
              </button>

              {statusOpen && (
                <div className="absolute left-0 top-[48px] z-30 w-[165px] overflow-hidden rounded-[8px] bg-white p-2 shadow-[0_8px_22px_rgba(0,0,0,0.13)]">
                  {(['All Status', 'Confirmed', 'Upcoming', 'Cancelled'] as const).map(
                    (status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => {
                          setStatusFilter(status)
                          setStatusOpen(false)
                        }}
                        className={`block h-9 w-full rounded-[6px] px-3 text-left font-host text-[13px] transition hover:bg-[#f4f4f4] ${
                          statusFilter === status
                            ? 'bg-[#fbeaea] font-medium text-[#333333]'
                            : 'text-[#555555]'
                        }`}
                      >
                        {status}
                      </button>
                    ),
                  )}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setDateOpen((open) => !open)
                  setStatusOpen(false)
                }}
                className="inline-flex h-[42px] items-center gap-2.5 rounded-[7px] bg-white px-5 font-host text-[13px] font-medium text-[#161616] shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
              >
                <CalendarDays className="h-4 w-4 stroke-[2]" />
                {dateFilter}
                <ChevronDown className="h-4 w-4 stroke-[2]" />
              </button>

              {dateOpen && (
                <div className="absolute right-0 top-[48px] z-30 w-[165px] overflow-hidden rounded-[8px] bg-white p-2 shadow-[0_8px_22px_rgba(0,0,0,0.13)]">
                  {(['Date Range', 'This Week', 'Past'] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        setDateFilter(option)
                        setDateOpen(false)
                      }}
                      className={`block h-9 w-full rounded-[6px] px-3 text-left font-host text-[13px] transition hover:bg-[#f4f4f4] ${
                        dateFilter === option
                          ? 'bg-[#fbeaea] font-medium text-[#333333]'
                          : 'text-[#555555]'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div> */}

          {/* <div className="mt-4 space-y-4">
            {loading ? (
              <div className="grid min-h-[210px] place-items-center rounded-[12px] bg-white text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <p className="font-host text-[14px] text-[#888888]">Loading trips...</p>
              </div>
            ) : filteredTrips.length > 0 ? (
              filteredTrips.map((trip) => (
                <TripCard
                  key={trip.id}
                  trip={trip}
                  onViewDetails={() => openDetails(trip.id)}
                  onCancel={() => openCancellation(trip.id)}
                />
              ))
            ) : (
              <div className="grid min-h-[210px] place-items-center rounded-[12px] bg-white text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <div>
                  <p className="font-host text-[18px] font-semibold text-[#333333]">
                    No trips found
                  </p>
                  <p className="mt-1.5 font-host text-[14px] text-[#888888]">
                    Try selecting a different status.
                  </p>
                </div>
              </div>
            )}
          </div> */}

          <div className="mt-4 space-y-4">
            {loading ? (
              <div className="grid min-h-[210px] place-items-center rounded-[12px] bg-white text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <p className="font-host text-[14px] text-[#888888]">
                  Loading trips...
                </p>
              </div>
            ) : trips.length > 0 ? (
              trips.map((trip) => (
                <TripCard
                  key={trip.id}
                  trip={trip}
                  onViewDetails={() => openDetails(trip.id)}
                  onCancel={() => openCancellation(trip.id)}
                />
              ))
            ) : (
              <div className="grid min-h-[210px] place-items-center rounded-[12px] bg-white text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <div>
                  <p className="font-host text-[18px] font-semibold text-[#333333]">
                    No trips found
                  </p>
                  <p className="mt-1.5 font-host text-[14px] text-[#888888]">
                    No bookings are available for this account.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      </ProfileModalShell>

      {overlayStep === 'details' && selectedTrip && (
        <BookingDetailsModal trip={selectedTrip} onClose={closeOverlay} />
      )}

      {(overlayStep === 'confirm' || overlayStep === 'success') && selectedTrip && (
        <CancellationModal
          step={overlayStep}
          trip={selectedTrip}
          reason={reason}
          policyAccepted={policyAccepted}
          onReasonChange={setReason}
          onPolicyChange={setPolicyAccepted}
          onClose={closeOverlay}
          onKeepBooking={closeOverlay}
          onConfirm={confirmCancellation}
          onBackToTrips={closeOverlay}
          onBookAnother={() => navigate('/day-trips')}
        />
      )}
    </>
  )
}

type TripCardProps = {
  trip: Trip
  onViewDetails: () => void
  onCancel: () => void
}

function TripCard({ trip, onViewDetails, onCancel }: TripCardProps) {
  const statusClasses: Record<TripStatus, string> = {
    Confirmed: 'border border-[#7CBC3D] bg-[#C8E6A8] text-[#4C9519]',
    Upcoming: 'border border-[#F0A878] bg-[#FFC3A5] text-[#A85127]',
    Cancelled: 'border border-[#E89B82] bg-white text-[#C9613E]',
    Completed: 'border border-[#3B82F6] bg-[#EFF6FF] text-[#1D4ED8]',
  }

  const canCancel = trip.status === 'Upcoming'
  const isCompleted = trip.status === 'Completed' || String(trip.status).toLowerCase() === 'completed'
  const reviewed = isTripReviewed(trip.id)
  const reviewData = reviewed ? getTripReview(trip.id) : null

  return (
    <article className="flex min-h-[112px] flex-col gap-3 rounded-[11px] bg-white px-4 py-3.5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:flex-row sm:items-center sm:gap-5">
      {/* Icon tile instead of photo */}
      <div className="grid h-[72px] w-[72px] shrink-0 place-items-center rounded-[10px] bg-[#F3F3F3] text-[#365E92]">
        <CarFront className="h-8 w-8 stroke-[1.8]" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-host text-[14px] font-medium text-[#333333]">
              {trip.reference}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-host text-[13px] font-medium text-[#202020]">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4 stroke-[2]" />
                {trip.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="h-4 w-4 stroke-[2]" />
                {trip.time}
              </span>
            </div>
          </div>

          <span
            className={`inline-flex h-[28px] items-center rounded-full px-4 font-host text-[12px] font-medium ${statusClasses[trip.status]}`}
          >
            {trip.status}
          </span>
        </div>

        <div className="mt-2.5 flex min-w-0 flex-wrap items-center gap-2 font-host text-[14px] font-semibold text-[#171717]">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <MapPin className="h-4 w-4 shrink-0 fill-black stroke-[1.8]" />
            <span className="truncate">{trip.from}</span>
          </span>
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#EEEEEE] text-[#A8A8A8]">
            <ArrowRight className="h-3.5 w-3.5 stroke-[2]" />
          </span>
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <MapPin className="h-4 w-4 shrink-0 fill-black stroke-[1.8]" />
            <span className="truncate">{trip.to}</span>
          </span>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-4 border-t border-[#F0F0F0] pt-3 sm:w-[168px] sm:flex-col sm:items-end sm:justify-center sm:border-0 sm:pt-0">
        <div className="text-right">
          <p
            className={`font-host text-[13px] font-medium ${
              trip.paid ? 'text-[#74A933]' : 'text-[#C9613E]'
            }`}
          >
            {trip.paid ? 'Paid' : 'Unpaid'}
          </p>
          <p className="mt-0.5 font-host text-[18px] font-medium text-[#222222]">
            €{trip.price}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={onViewDetails}
            className="whitespace-nowrap font-host text-[12px] font-medium text-[#496EE4] underline underline-offset-2 transition hover:text-[#294FC6]"
          >
            View Details
          </button>
          {isCompleted && (
            reviewed ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 font-host text-[12px] font-semibold text-amber-700">
                <Star className="h-3 w-3 fill-amber-400 stroke-amber-500" />
                {reviewData?.rating ? `${reviewData.rating}/5 Reviewed` : 'Reviewed'}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(
                    new CustomEvent(TRIP_REVIEW_TRIGGER_EVENT, { detail: { trip } })
                  )
                }}
                className="h-[31px] whitespace-nowrap rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-3.5 font-host text-[12px] font-semibold text-white shadow-sm hover:from-amber-600 hover:to-amber-700 active:scale-[0.98] transition flex items-center gap-1.5"
              >
                <Star className="h-3.5 w-3.5 fill-white stroke-white" />
                Rate & Review
              </button>
            )
          )}
          {canCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="h-[31px] whitespace-nowrap rounded-full bg-[#D53030] px-4 font-host text-[12px] font-medium text-white transition hover:bg-[#BD2222] active:scale-[0.98]"
            >
              Cancel Trip
            </button>
          )}
        </div>
      </div>
    </article>
  )
}

type BookingDetailsModalProps = {
  trip: Trip
  onClose: () => void
}

function BookingDetailsModal({ trip, onClose }: BookingDetailsModalProps) {
  const statusClasses: Record<TripStatus, string> = {
    Confirmed: 'border border-[#7CBC3D] bg-[#C8E6A8] text-[#4C9519]',
    Upcoming: 'border border-[#F0A878] bg-[#FFC3A5] text-[#A85127]',
    Cancelled: 'border border-[#E89B82] bg-white text-[#C9613E]',
    Completed: 'border border-[#3B82F6] bg-[#EFF6FF] text-[#1D4ED8]',
  }

  return (
    <ModalBackdrop onClose={onClose} labelledBy="booking-details-title">
      <div className="relative w-full max-w-[480px] overflow-hidden rounded-[14px] bg-white p-5 shadow-[0_18px_55px_rgba(0,0,0,0.2)] sm:p-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close booking details"
          className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full border-[2px] border-[#91ADFF] text-[#5F6570] transition hover:bg-[#F4F6FF]"
        >
          <X className="h-4 w-4 stroke-[2]" />
        </button>

        <h2
          id="booking-details-title"
          className="pr-10 font-host text-[20px] font-semibold tracking-[-0.03em] text-[#111111]"
        >
          Booking Details
        </h2>

        <div className="mt-4 flex items-start gap-3">
          <div className="grid h-[64px] w-[64px] shrink-0 place-items-center rounded-[10px] bg-[#F3F3F3] text-[#365E92]">
            <CarFront className="h-7 w-7 stroke-[1.8]" />
          </div>

          <div className="min-w-0 flex-1 font-host text-[#111111]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[14px] font-medium">{trip.reference}</p>
              <span
                className={`inline-flex h-[26px] items-center rounded-full px-3 text-[12px] font-medium ${statusClasses[trip.status]}`}
              >
                {trip.status}
              </span>
            </div>
            <p className="mt-1 text-[16px] font-semibold tracking-[-0.02em]">
              {trip.from}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] font-medium">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4 stroke-[2]" />
                {trip.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="h-4 w-4 stroke-[2]" />
                {trip.time}
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[#EEEEEE] px-2.5 text-[12px] font-medium">
                <Users className="h-3.5 w-3.5 stroke-[2]" />
                {trip.passengers}
              </span>
              <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[#EEEEEE] px-2.5 text-[12px] font-medium">
                <Briefcase className="h-3.5 w-3.5 stroke-[2]" />
                {trip.luggage}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-[10px] bg-[#F7F7F7] px-3.5 py-3">
          <div className="flex items-start gap-2.5">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 fill-black stroke-[1.8]" />
            <div>
              <p className="font-host text-[11px] font-semibold uppercase text-[#8A8A8A]">
                Pickup
              </p>
              <p className="mt-0.5 font-host text-[13px] text-[#333333]">
                {trip.fromAddress}
              </p>
            </div>
          </div>
          <div className="my-2.5 ml-[7px] h-4 w-px bg-[#D0D0D0]" />
          <div className="flex items-start gap-2.5">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 fill-black stroke-[1.8]" />
            <div>
              <p className="font-host text-[11px] font-semibold uppercase text-[#8A8A8A]">
                Drop-off
              </p>
              <p className="mt-0.5 font-host text-[13px] text-[#333333]">
                {trip.toAddress}
              </p>
            </div>
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 font-host text-[13px]">
          <div>
            <dt className="text-[#8A8A8A]">Customer</dt>
            <dd className="mt-0.5 font-medium text-[#111111]">{trip.customerName}</dd>
          </div>
          <div>
            <dt className="text-[#8A8A8A]">Vehicle</dt>
            <dd className="mt-0.5 font-medium text-[#111111]">{trip.vehicle}</dd>
          </div>
          <div>
            <dt className="text-[#8A8A8A]">Phone</dt>
            <dd className="mt-0.5 font-medium text-[#111111]">{trip.phone}</dd>
          </div>
          <div>
            <dt className="text-[#8A8A8A]">Payment</dt>
            <dd className="mt-0.5 font-medium text-[#111111]">
              {trip.paid ? 'Paid' : 'Unpaid'} · €{trip.price}
            </dd>
          </div>
        </dl>

        <button
          type="button"
          onClick={onClose}
          className="mt-5 h-[42px] w-full rounded-[9px] bg-black font-host text-[14px] font-semibold text-white transition hover:bg-[#202020] active:scale-[0.99]"
        >
          Close
        </button>
      </div>
    </ModalBackdrop>
  )
}

type CancellationModalProps = {
  step: 'confirm' | 'success'
  trip: Trip
  reason: string
  policyAccepted: boolean
  onReasonChange: (value: string) => void
  onPolicyChange: (checked: boolean) => void
  onClose: () => void
  onKeepBooking: () => void
  onConfirm: () => void
  onBackToTrips: () => void
  onBookAnother: () => void
}

function CancellationModal({
  step,
  trip,
  reason,
  policyAccepted,
  onReasonChange,
  onPolicyChange,
  onClose,
  onKeepBooking,
  onConfirm,
  onBackToTrips,
  onBookAnother,
}: CancellationModalProps) {
  return (
    <ModalBackdrop onClose={onClose} labelledBy="cancellation-title">
      {step === 'confirm' ? (
        <div className="relative w-full max-w-[440px] overflow-hidden rounded-[11px] bg-white px-5 pb-5 pt-6 shadow-[0_16px_45px_rgba(0,0,0,0.18)] sm:px-6">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close cancellation dialog"
            className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full border-[2px] border-[#90adff] text-[#5f6570] transition hover:bg-[#f4f6ff]"
          >
            <X className="h-4 w-4 stroke-[2]" />
          </button>

          <div className="text-center">
            <h2
              id="cancellation-title"
              className="font-host text-[22px] font-semibold tracking-[-0.03em] text-black"
            >
              Cancel Booking
            </h2>
            <p className="mx-auto mt-1.5 max-w-[290px] font-host text-[16px] leading-[1.22] text-[#706b6b]">
              Are you sure you want to cancel
              <br />
              this booking?
            </p>

            <div className="mx-auto mt-2.5 grid h-[82px] w-[82px] place-items-center rounded-full bg-[#fafafa]">
              <span className="grid h-[43px] w-[43px] place-items-center rounded-full bg-[#cf0000] text-white">
                <X className="h-7 w-7 stroke-[4]" />
              </span>
            </div>
          </div>

          <ModalTripSummary trip={trip} />

          <div className="mt-4">
            <h3 className="font-host text-[15px] font-medium text-[#686262]">
              Cancellation Policy
            </h3>
            <ul className="mt-1 space-y-1 pl-5 font-host text-[13px] leading-[1.4] text-[#b99d9d]">
              <li className="list-disc">Free cancellation up to 24 hours before pickup.</li>
              <li className="list-disc">
                Refunds are processed according to our cancellation policy.
              </li>
            </ul>
          </div>

          <label className="mt-4 block">
            <span className="font-host text-[15px] font-semibold text-[#282222]">
              Cancellation Reason (Optional)
            </span>
            <textarea
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
              className="mt-2 h-[82px] w-full resize-none rounded-[10px] border-0 bg-[#efefef] p-3 font-host text-[13px] text-[#333333] outline-none ring-1 ring-transparent transition focus:ring-[#b9b9b9]"
              aria-label="Cancellation reason"
            />
          </label>

          <label className="mt-2.5 flex cursor-pointer items-center gap-2.5 font-host text-[14px] text-[#171717]">
            <input
              type="checkbox"
              checked={policyAccepted}
              onChange={(event) => onPolicyChange(event.target.checked)}
              className="h-5 w-5 shrink-0 cursor-pointer rounded-[4px] border border-[#dedede] accent-[#c91e1e]"
            />
            I understand the cancellation policy.
          </label>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 sm:gap-7">
            <button
              type="button"
              onClick={onKeepBooking}
              className="h-[44px] rounded-[9px] bg-white font-host text-[14px] font-semibold text-[#111111] shadow-[0_2px_8px_rgba(0,0,0,0.1)] transition hover:bg-[#fafafa] active:scale-[0.99]"
            >
              Keep Booking
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!policyAccepted}
              className="h-[44px] rounded-[9px] bg-[#c91d1d] px-4 font-host text-[14px] font-semibold text-white transition hover:bg-[#b91616] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-45"
            >
              Confirm Cancellation
            </button>
          </div>
        </div>
      ) : (
        <div className="relative w-full max-w-[390px] rounded-[11px] bg-white px-4 pb-5 pt-5 shadow-[0_16px_45px_rgba(0,0,0,0.18)]">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close cancelled booking dialog"
            className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full border-[2px] border-[#90adff] text-[#5f6570] transition hover:bg-[#f4f6ff]"
          >
            <X className="h-4 w-4 stroke-[2]" />
          </button>

          <div className="text-center">
            <div className="mx-auto grid h-[86px] w-[86px] place-items-center rounded-full bg-[#fafafa]">
              <span className="grid h-[44px] w-[44px] place-items-center rounded-full bg-[#cf0000] text-white">
                <X className="h-7 w-7 stroke-[4]" />
              </span>
            </div>
            <h2
              id="cancellation-title"
              className="mt-2 font-host text-[20px] font-semibold tracking-[-0.025em] text-[#111111]"
            >
              Booking Cancelled
            </h2>
            <p className="mt-1 font-host text-[14px] text-[#746e6e]">
              Your booking has been cancelled successfully.
            </p>
          </div>

          <div className="mx-auto mt-4 flex max-w-[345px] items-center gap-3 rounded-[11px] bg-[#E9E9E9] p-3">
            <div className="grid h-[72px] w-[72px] shrink-0 place-items-center rounded-[9px] bg-white text-[#365E92]">
              <CarFront className="h-8 w-8 stroke-[1.8]" />
            </div>
            <div className="min-w-0 font-host text-[#111111]">
              <p className="text-[14px] font-medium">{trip.reference}</p>
              <p className="mt-1 text-[14px] font-medium">{trip.bookingReference}</p>
              <p className="mt-3 text-[13px] font-semibold">Refund status</p>
              <p className="mt-1 text-[12px]">Processing (3-7 days)</p>
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <button
              type="button"
              onClick={onBackToTrips}
              className="h-[44px] w-full rounded-[9px] bg-[#c91d1d] font-host text-[15px] font-semibold text-white transition hover:bg-[#b91616] active:scale-[0.99]"
            >
              Back to My Trips
            </button>
            <button
              type="button"
              onClick={onBookAnother}
              className="h-[44px] w-full rounded-[9px] bg-white font-host text-[15px] font-semibold text-[#111111] shadow-[0_2px_8px_rgba(0,0,0,0.1)] transition hover:bg-[#fafafa] active:scale-[0.99]"
            >
              Book Another Trip
            </button>
          </div>
        </div>
      )}
    </ModalBackdrop>
  )
}

function ModalTripSummary({ trip }: { trip: Trip }) {
  return (
    <div className="mt-2.5 flex items-center gap-3 rounded-[11px] bg-[#EFEFEF] p-2.5">
      <div className="grid h-[64px] w-[64px] shrink-0 place-items-center rounded-[8px] bg-white text-[#365E92]">
        <CarFront className="h-7 w-7 stroke-[1.8]" />
      </div>
      <div className="min-w-0 font-host text-[#111111]">
        <p className="text-[13px] font-medium">{trip.reference}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
          <span className="inline-flex items-center gap-1">
            <CalendarDays className="h-3.5 w-3.5 stroke-[2]" />
            {trip.date}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock3 className="h-3.5 w-3.5 stroke-[2]" />
            {trip.time}
          </span>
        </div>
        <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5 text-[12px] font-semibold">
          <span className="inline-flex min-w-0 items-center gap-1">
            <MapPin className="h-4 w-4 shrink-0 fill-black stroke-[1.8]" />
            <span className="truncate">{trip.from}</span>
          </span>
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#DEDEDE] text-[#9A9A9A]">
            <ArrowRight className="h-3.5 w-3.5 stroke-[2]" />
          </span>
          <span className="inline-flex min-w-0 items-center gap-1">
            <MapPin className="h-4 w-4 shrink-0 fill-black stroke-[1.8]" />
            <span className="truncate">{trip.to}</span>
          </span>
        </div>
      </div>
    </div>
  )
}

type ModalBackdropProps = {
  children: React.ReactNode
  onClose: () => void
  labelledBy: string
}

function ModalBackdrop({ children, onClose, labelledBy }: ModalBackdropProps) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 px-4 py-5 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      {children}
    </div>
  )
}
